import { useMemo } from 'react';
import { useTranslation } from '@/i18n/useTranslation';
import styles from './WizardStepper.module.css';

const INTAKE_STEP_KEYS = ['venture', 'contact', 'review', 'consent'] as const;

const JOURNEY_STEP_IDS = ['map', ...INTAKE_STEP_KEYS, 'assessment'] as const;

type StepState = 'done' | 'active' | 'pending';

type StepItem = {
  id: string;
  label: string;
  state: StepState;
  marker: string;
};

export interface WizardStepperProps {
  /** Intake substep 0–3 (standalone wizard only). */
  currentStep?: number;
  variant?: 'standalone' | 'journey';
  /** Global journey index 0–5: map, assessment, then intake steps. */
  journeyActiveIndex?: number;
}

function buildJourneySteps(
  journeyActiveIndex: number,
  t: ReturnType<typeof useTranslation>,
): StepItem[] {
  const labels: Record<(typeof JOURNEY_STEP_IDS)[number], string> = {
    map: t.wizard.journeyStepsCompact.map,
    assessment: t.wizard.journeyStepsCompact.assessment,
    venture: t.wizard.steps.venture,
    contact: t.wizard.steps.contact,
    review: t.wizard.steps.review,
    consent: t.wizard.steps.consent,
  };

  return JOURNEY_STEP_IDS.map((id, index) => {
    const state: StepState =
      index < journeyActiveIndex
        ? 'done'
        : index === journeyActiveIndex
          ? 'active'
          : 'pending';
    return {
      id,
      label: labels[id],
      state,
      marker: state === 'done' ? '✓' : String(index + 1),
    };
  });
}

export default function WizardStepper({
  currentStep = 0,
  variant = 'standalone',
  journeyActiveIndex = 0,
}: WizardStepperProps) {
  const t = useTranslation();

  const steps = useMemo((): StepItem[] => {
    if (variant === 'journey') {
      return buildJourneySteps(journeyActiveIndex, t);
    }

    return INTAKE_STEP_KEYS.map((key, index) => {
      const state: StepState =
        index < currentStep ? 'done' : index === currentStep ? 'active' : 'pending';
      return {
        id: key,
        label: t.wizard.steps[key],
        state,
        marker: state === 'done' ? '✓' : String(index + 1),
      };
    });
  }, [currentStep, journeyActiveIndex, t, variant]);

  const ariaLabel = variant === 'journey' ? t.wizard.journeyStepperAria : t.wizard.stepperAria;

  return (
    <nav className={styles.stepper} aria-label={ariaLabel} data-testid="journey-stepper">
      <ol className={`${styles.list} ${variant === 'journey' ? styles.listJourney : ''}`}>
        {steps.map((step, index) => (
          <li
            key={step.id}
            className={`${styles.step} ${step.state === 'active' ? styles.stepActive : ''} ${step.state === 'done' ? styles.stepDone : ''} ${step.state === 'pending' ? styles.stepPending : ''}`}
            aria-current={step.state === 'active' ? 'step' : undefined}
          >
            <span className={styles.number} aria-hidden>
              {step.marker}
            </span>
            <span className={styles.label}>{step.label}</span>
            {index < steps.length - 1 ? (
              <span className={styles.divider} aria-hidden />
            ) : null}
          </li>
        ))}
      </ol>
    </nav>
  );
}
